<template>
  <SoAlert
    v-if="queryError"
    title="Failed to render meter readings table"
    data-testid="meter-readings-error"
  />

  <SoDataTableServer
    v-else
    ref="dataTable"
    v-model:options="tableOptions"
    :headers="headers"
    :data="tableData"
    searchable
    data-testid="meter-readings-list-table"
    @fetch="fetchData"
  >
    <template #itemActions="rowData">
      <div class="d-flex gap-1 justify-end">
        <SoButton
          title="Update"
          type="success"
          data-testid="reading-update"
          @click="openUpdateModal(rowData)"
        />
      </div>
    </template>

    <template #expandableData="rowData">
      <tr v-for="(error, index) in rowData.debugData" :key="index">
        <td :colspan="8">{{ error.errorCode }} - {{ error.errorMessage }}</td>
      </tr>
      <tr
        v-if="rowData.comment"
        data-testid="meter-readings-list-table-comment"
      >
        <td :colspan="8">Comment - {{ rowData.comment }}</td>
      </tr>
    </template>
  </SoDataTableServer>
  <MeterReadingsUpdateModal
    v-if="currentRow"
    v-model="isUpdateModalOpen"
    :current-row="currentRow"
    @submitted="refetchTableData()"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useSnackbarStore } from "@/stores/snackbar"
  import SoDataTableServer, {
    SoDataTableData,
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import {
    FilterDateRange,
    FilterCheckbox,
    FilterType,
    SoDataTableColumnType,
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import SoButton from "@/components/formControls/SoButton.vue"
  import {
    MeterReadingFilters,
    MeterReadingsService,
    MeterReadingQueryResult,
  } from "@/services/operations/MeterReadingsService"
  import {
    IMeterReading,
    MeterReadingStatus,
  } from "@/models/operations/meterReading/meterReading.types"
  import MeterReadingsUpdateModal from "@/views/operations/meterReadings/components/MeterReadingsUpdateModal.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { cloneDeep, startCase } from "lodash-es"
  import { QueryResult } from "@/api/utils/types"
  import { safeDayjs } from "@/helpers/dateHelper"

  type TableFilters = {
    status: FilterCheckbox<MeterReadingStatus>
    errorCode: FilterCheckbox<MeterReadingStatus>
    submissionDate: FilterDateRange
    lastReadings: FilterCheckbox
  }

  type SoDataTableOptionsFetch = SoDataTableOptions & {
    hasAnyFilters: boolean
  }

  const headers: SoDataTableHeader[] = [
    {
      title: "Email",
      key: "userEmail",
      sortable: false,
      type: SoDataTableColumnType.COPY_TO_CLIPBOARD,
    },
    {
      title: "Account Number",
      key: "accountPath",
      sortable: false,
      type: SoDataTableColumnType.LINK,
    },
    {
      title: "Submission Value",
      key: "mappedSubmissionValues",
      type: SoDataTableColumnType.LIST,
    },
    {
      title: "Submitted At",
      key: "createdAt",
      type: SoDataTableColumnType.DATE,
      sortable: false,
    },
    {
      title: "Status",
      key: "mappedStatus",
      type: SoDataTableColumnType.STATUS,
      sortable: false,
    },
    {
      title: "Error",
      key: "errorCodes",
      sortable: false,
    },
  ]
  const statuses: Record<MeterReadingStatus, string> = {
    ERRORED: "Errored",
    MANUAL_SUBMISSION: "Manual submission",
    CONTACTED: "Contacted",
    NO_ACTION_REQUIRED: "No action required",
  }

  export default defineComponent({
    components: {
      SoAlert,
      MeterReadingsUpdateModal,
      SoButton,
      SoDataTableServer,
    },
    data() {
      return {
        isUpdateModalOpen: false,
        snackbar: useSnackbarStore(),
        currentRow: null as IMeterReading | null,
        headers,
        tableOptions: {
          filters: {
            status: {
              type: FilterType.CHECKBOX,
              label: "Status",
              value: [],
              options: statuses,
              expandable: true,
            },
            errorCode: {
              type: FilterType.CHECKBOX,
              label: "Error Code",
              value: [],
              options: {},
              expandable: true,
            },
            submissionDate: {
              type: FilterType.DATE_RANGE,
              label: "Date",
              value: {
                from: null,
                to: null,
              },
              expandable: true,
            },
            lastReadings: {
              type: FilterType.CHECKBOX,
              label: "Show all readings",
              value: [],
              expandable: false,
              options: { true: "true" },
            },
          } as TableFilters,
        },
        query: null as null | QueryResult<MeterReadingQueryResult>,
        meterReadingsErrorsQuery: MeterReadingsService.getMeterReadingsErrors(),
      }
    },
    computed: {
      tableData(): SoDataTableData {
        return {
          isLoading: !!this.query?.isFetching,
          totalItems: this.query?.data?.totalCount ?? 0,
          items:
            (this.query?.data?.records as unknown as SoDataTableItem[]) ?? [],
        }
      },
      queryError() {
        return this.query?.isError
      },
      meterReadingsErrors() {
        const errors = {}
        if (this.meterReadingsErrorsQuery?.data?.length) {
          for (const errorCode of this.meterReadingsErrorsQuery.data) {
            errors[errorCode] = startCase(errorCode)
          }
        }

        return errors
      },
    },
    watch: {
      meterReadingsErrors(errors) {
        if (!errors) return

        this.processMeterReadingsErrors(errors)
      },
    },
    methods: {
      processMeterReadingsErrors(errors) {
        const filters = cloneDeep(this.tableOptions.filters) as TableFilters

        filters.errorCode.options = errors

        const dataTable = this.$refs.dataTable as InstanceType<
          typeof SoDataTableServer
        >
        if (dataTable.setOptions) {
          dataTable.setOptions({ filters }, false)
        }
      },
      mapFilters(filters: TableFilters): MeterReadingFilters {
        const toDate = (date: Date | null | undefined) =>
          date ? safeDayjs(date)?.add(1, "day") : undefined

        return {
          status: filters.status.value ?? [],
          submissionDate: {
            from: safeDayjs(filters.submissionDate.value.from),
            to: toDate(filters.submissionDate.value.to),
          },
          errorCode: filters.errorCode.value ?? [],
        }
      },
      fetchData({
        filters,
        hasAnyFilters,
        page,
        pageSize,
        search,
      }: SoDataTableOptionsFetch) {
        // Business requirement to show last meter readings if filters were not applied
        if (hasAnyFilters) {
          this.query = MeterReadingsService.getMeterReadings(
            search,
            this.mapFilters(filters as TableFilters),
            page,
            pageSize,
          )
        } else {
          this.query = MeterReadingsService.getLastMeterReadings(page, pageSize)
        }
      },
      openUpdateModal(rowData) {
        this.currentRow = rowData.item
        this.isUpdateModalOpen = true
      },
      async refetchTableData() {
        this.query?.refetch()
      },
    },
  })
</script>
